import { useEffect, useRef, useState } from "react";
import { Rocket, Clock, Zap } from "lucide-react";

/** Live countdown card shown to Bank Staff on their Wanted List page. */
export default function BoostCountdownCard({ boost, onWindowChange }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const startMs = boost ? new Date(boost.start_iso).getTime() : 0;
  const endMs   = boost ? new Date(boost.end_iso).getTime()   : 0;
  const isLive  = !!boost && now >= startMs && now < endMs;
  const isEnded = !!boost && now >= endMs;
  const targetMs = isLive ? endMs : startMs;
  const delta = boost ? Math.max(0, targetMs - now) : 0;
  const h = Math.floor(delta / 3_600_000);
  const m = Math.floor((delta % 3_600_000) / 60_000);
  const s = Math.floor((delta % 60_000) / 1000);
  const pad = (n) => String(n).padStart(2, "0");

  const phase = !boost ? "none" : isEnded ? "ended" : isLive ? "live" : "upcoming";
  const lastPhase = useRef(phase);
  useEffect(() => {
    if (lastPhase.current !== phase) {
      onWindowChange && onWindowChange(phase);
      lastPhase.current = phase;
    }
  }, [phase, onWindowChange]);

  if (!boost) {
    return (
      <div className="card-surface p-5 border-l-4 border-slate-700" data-testid="boost-card-none">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
            <Rocket className="w-5 h-5 text-slate-500"/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Wanted List</div>
            <div className="text-white font-heading text-base">No cases scheduled</div>
            <div className="text-slate-500 text-xs mt-0.5">Cases will appear when Admin boosts your district.</div>
          </div>
        </div>
      </div>
    );
  }

  if (isEnded) {
    return (
      <div className="card-surface p-5 border-l-4 border-slate-600" data-testid="boost-card-ended">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
            <Clock className="w-5 h-5 text-slate-500"/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Boost Window</div>
            <div className="text-white font-heading text-base">Window Closed</div>
            <div className="text-slate-500 text-xs mt-0.5">The last boost ended at {boost.end_time} IST.</div>
          </div>
        </div>
      </div>
    );
  }

  if (isLive) {
    return (
      <div className="card-surface p-5 border-l-4 border-emerald-500 pulse-gold" data-testid="boost-card-live">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
              <Zap className="w-5 h-5 text-emerald-400"/>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">LIVE — Cases Available</div>
              <div className="text-white font-heading text-base">{boost.date_label} · {boost.start_time} – {boost.end_time} IST</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Closes in</div>
            <div className="font-mono-alt text-2xl text-emerald-300 font-bold" data-testid="boost-timer">{pad(h)}h {pad(m)}m {pad(s)}s</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card-surface p-5 border-l-4 border-orange-500" data-testid="boost-card-upcoming">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-500/20 border border-orange-500/40 flex items-center justify-center">
            <Rocket className="w-5 h-5 text-orange-400"/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold">Next Case View</div>
            <div className="text-white font-heading text-base">{boost.date_label} · {boost.start_time} – {boost.end_time} IST</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Time remaining</div>
          <div className="font-mono-alt text-2xl text-orange-300 font-bold" data-testid="boost-timer">{pad(h)}h {pad(m)}m {pad(s)}s</div>
        </div>
      </div>
    </div>
  );
}
